import { useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { useStore } from "../store.ts";
import { Ekran, Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";

/**
 * Trzy akcje administratora, bez osobnego panelu.
 *
 * Zapraszanie, reset hasła i dezaktywacja konta — tylko to, co realnie blokuje.
 * Rodzina to nie korporacja: role są flagą `boolean`, a nie systemem uprawnień.
 */
export function Admin({ onWroc }: { onWroc: () => void }) {
  const { user } = useStore();
  const [konta, setKonta] = useState<Array<{ id: string; nick: string }>>([]);
  const [link, setLink] = useState<{ url: string; opis: string } | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  const odswiez = () =>
    api
      .users()
      .then(({ users }) => setKonta(users))
      .catch(() => undefined);

  useEffect(() => {
    void odswiez();
  }, []);

  /**
   * Serwer zwraca sam token, nie gotowy adres — nie zna adresu, spod którego
   * korzysta klient (produkcja czy telefon w LAN), a zgadywanie go z nagłówka
   * `Host` byłoby zaproszeniem do podszycia się. Adres składamy tutaj.
   */
  const zbudujLink = (token: string) => `${window.location.origin}/z/${token}`;

  const zapros = async () => {
    setBlad(null);
    try {
      const { token } = await api.createInvite("register");
      setLink({ url: zbudujLink(token), opis: "Link do założenia konta (ważny 7 dni)" });
    } catch (error) {
      setBlad(error instanceof ApiError ? error.message : "Nie udało się.");
    }
  };

  const resetHasla = async (userId: string, nick: string) => {
    setBlad(null);
    try {
      const { token } = await api.createInvite("password_reset", userId);
      setLink({ url: zbudujLink(token), opis: `Link do zmiany hasła dla ${nick}` });
    } catch (error) {
      setBlad(error instanceof ApiError ? error.message : "Nie udało się.");
    }
  };

  const dezaktywuj = async (userId: string) => {
    setBlad(null);
    try {
      await api.setUserActive(userId, false);
      await odswiez();
    } catch (error) {
      setBlad(error instanceof ApiError ? error.message : "Nie udało się.");
    }
  };

  const kopiuj = async (tekst: string) => {
    try {
      await navigator.clipboard.writeText(tekst);
      useStore.getState().showToast("Skopiowano — wklej na WhatsAppa");
    } catch {
      // Bez uprawnienia do schowka zostaje zaznaczenie ręczne; link jest widoczny.
    }
  };

  return (
    <Ekran
      tytul="Konta"
      akcja={
        <Przycisk wariant="cichy" onClick={onWroc}>
          Wróć
        </Przycisk>
      }
      stopka={
        <Przycisk wariant="glowny" pelna onClick={() => void zapros()}>
          Zaproś nową osobę
        </Przycisk>
      }
    >
      {blad ? <Komunikat rodzaj="blad">{blad}</Komunikat> : null}

      {link ? (
        <Karta>
          <div className="text-sm text-tekst-slaby mb-2">{link.opis}</div>
          <div className="break-all text-sm bg-tlo rounded-xl p-3 mb-3">{link.url}</div>
          <div className="flex gap-2">
            <Przycisk wariant="glowny" onClick={() => void kopiuj(link.url)}>
              Kopiuj
            </Przycisk>
            <Przycisk wariant="cichy" onClick={() => setLink(null)}>
              Zamknij
            </Przycisk>
          </div>
        </Karta>
      ) : null}

      {konta.map((konto) => (
        <Karta key={konto.id}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="font-semibold">{konto.nick}</span>
            {konto.id === user?.id ? <Odznaka aktywna>to Ty</Odznaka> : null}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Przycisk onClick={() => void resetHasla(konto.id, konto.nick)}>Reset hasła</Przycisk>
            {konto.id === user?.id ? null : (
              <Przycisk wariant="blad" onClick={() => void dezaktywuj(konto.id)}>
                Dezaktywuj
              </Przycisk>
            )}
          </div>
        </Karta>
      ))}
    </Ekran>
  );
}
